import { getAppointmentTimingStatus, isAppointmentOperationallyComplete } from '@/lib/appointmentCardPresentation';
import { getAppointmentDisplayStatus } from '@/lib/work-items/appointmentRescheduleState';
import { isTerminalWorkItemStatus, normalizeWorkflowStatusKey } from '@/lib/work-items/operationalStatusConfig';
import { getWorkItemTypeRegistry } from '@/config/work-items/configLoader';
import { computeSystemStatus } from '@/lib/finance/documentLifecycle';

type RuntimeAppointmentState = 'en_route' | 'in_progress';

type WorkflowStateStatusContext = {
  current_state?: string | null;
  active_work_item_id?: string | number | null;
  traveling_to_work_item_id?: string | number | null;
};

type WorkItemStatusSource = {
  id?: string | number | null;
  item_type?: string | null;
  status?: string | null;
  payment_status?: string | null;
  scheduled_start?: string | null;
  scheduled_end?: string | null;
  completed_at?: string | null;
  resolved_at?: string | null;
  closed_at?: string | null;
  resolution_type?: string | null;
  custom_fields?: Record<string, unknown> | string | null;
  [key: string]: unknown;
};

export interface ResolvedWorkItemStatus {
  rawStatusKey: string;
  baseStatusKey: string;
  statusKey: string;
  label: string;
  source:
    | 'payment_status'
    | 'appointment_display'
    | 'raw_status'
    | 'runtime_timer'
    | 'workflow_state'
    | 'timing_status'
    | 'finance_system';
  timingStatus: string | null;
  isTerminal: boolean;
  isOperationallyComplete: boolean;
  payment_status: string | null;
  is_system_managed: boolean;
  finance_domain: boolean;
}

export interface ResolveWorkItemStatusOptions {
  privilegedViewer?: boolean;
  preferPaymentStatus?: boolean;
  includeTimingWarnings?: boolean;
  runtimeAppointmentState?: RuntimeAppointmentState | null;
  workflowState?: WorkflowStateStatusContext | null;
}

function formatStatusLabel(statusKey: string, item?: WorkItemStatusSource): string {
  if (statusKey === 'running_late') {
    const lateLabel = buildRunningLateLabel(item);
    return lateLabel || 'Late';
  }

  if (statusKey === 'behind_schedule') {
    const lateLabel = buildRunningLateLabel(item);
    return lateLabel ? lateLabel.replace(/^Late /, 'Behind ') : 'Behind Schedule';
  }

  if (statusKey === 'over_schedule') {
    return 'Over Schedule';
  }

  if (statusKey === 'travel_paused') {
    return 'Travel Paused';
  }

  if (statusKey === 'working') {
    return 'In Progress / Working';
  }

  if (statusKey === 'on_route') {
    return 'On Route';
  }

  if (statusKey === 'arrived') {
    return 'At Site';
  }

  return statusKey
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function parseDateTimeMs(value: unknown): number | null {
  if (typeof value !== 'string' && typeof value !== 'number' && !(value instanceof Date)) {
    return null;
  }

  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
}

function buildRunningLateLabel(item?: WorkItemStatusSource): string | null {
  const scheduledStartMs = parseDateTimeMs(item?.scheduled_start);
  if (scheduledStartMs == null) {
    return null;
  }

  const lateMinutes = Math.max(0, Math.round((Date.now() - scheduledStartMs) / 60000));
  if (lateMinutes <= 0) {
    return 'Late';
  }

  if (lateMinutes < 60) {
    return `Late ${lateMinutes}m`;
  }

  const hours = Math.floor(lateMinutes / 60);
  const remainderMinutes = lateMinutes % 60;
  return `Late ${hours}h ${remainderMinutes}m`;
}

function getRuntimeWorkflowOverlayStatus(
  item: WorkItemStatusSource,
  workflowState?: WorkflowStateStatusContext | null,
): string | null {
  if (!workflowState || item.id == null) {
    return null;
  }

  const itemId = String(item.id);
  const activeWorkItemId = workflowState.active_work_item_id != null
    ? String(workflowState.active_work_item_id)
    : null;
  const travelingToWorkItemId = workflowState.traveling_to_work_item_id != null
    ? String(workflowState.traveling_to_work_item_id)
    : null;
  const currentState = normalizeWorkflowStatusKey(workflowState.current_state);

  if (travelingToWorkItemId === itemId) {
    if (currentState === 'travel_paused') {
      return 'travel_paused';
    }

    if (currentState === 'traveling') {
      return 'on_route';
    }
  }

  if (activeWorkItemId === itemId) {
    if (currentState === 'arrived') {
      return 'arrived';
    }

    if (currentState === 'paused') {
      return 'paused';
    }

    if (currentState === 'checked_in' || currentState === 'completing') {
      return 'working';
    }
  }

  return null;
}

export function resolveWorkItemStatus(
  item: WorkItemStatusSource,
  options: ResolveWorkItemStatusOptions = {},
): ResolvedWorkItemStatus {
  const {
    privilegedViewer = false,
    preferPaymentStatus = false,
    includeTimingWarnings = true,
    runtimeAppointmentState = null,
    workflowState = null,
  } = options;

  const itemType = normalizeWorkflowStatusKey(item.item_type);
  const rawStatusKey = normalizeWorkflowStatusKey(item.status);
  const paymentStatusKey = normalizeWorkflowStatusKey(item.payment_status);
  const isAppointment = itemType === 'appointment';

  // Check if this is a finance domain item via configLoader
  const registry = getWorkItemTypeRegistry();
  const isFinanceDomain = registry.isFinanceDomain(itemType);

  let baseStatusKey = rawStatusKey;
  let source: ResolvedWorkItemStatus['source'] = 'raw_status';
  let resolvedPaymentStatus: string | null = paymentStatusKey;
  let isSystemManaged = false;

  if (isFinanceDomain) {
    // Finance domain: use computeSystemStatus for system-computed statuses
    const raw = item as Record<string, unknown>;
    const docLike = {
      item_type: itemType,
      status: item.status,
      payment_status: item.payment_status,
      grand_total: (raw.grand_total as number | string | null) ?? null,
      amount_paid: (raw.amount_paid as number | string | null) ?? null,
      balance_due: (raw.balance_due as number | string | null) ?? null,
      due_date: (raw.due_date as string | null) ?? null,
      expiry_date: (raw.expiry_date as string | null) ?? null,
    };
    const systemResult = computeSystemStatus(docLike);
    baseStatusKey = systemResult.status;
    resolvedPaymentStatus = systemResult.payment_status;
    source = 'finance_system';
    isSystemManaged = registry.isSystemStatus(itemType, baseStatusKey);
  } else if (preferPaymentStatus && paymentStatusKey) {
    baseStatusKey = paymentStatusKey;
    source = 'payment_status';
  } else if (isAppointment) {
    baseStatusKey = getAppointmentDisplayStatus(item as Record<string, unknown>, {
      privilegedViewer,
    }) || rawStatusKey;
    source = 'appointment_display';
  }

  let statusKey = baseStatusKey;

  if (runtimeAppointmentState === 'in_progress') {
    statusKey = 'in_progress';
    source = 'runtime_timer';
  } else if (runtimeAppointmentState === 'en_route') {
    statusKey = 'on_route';
    source = 'runtime_timer';
  } else {
    const workflowOverlayStatus = getRuntimeWorkflowOverlayStatus(item, workflowState);
    if (workflowOverlayStatus) {
      statusKey = workflowOverlayStatus;
      source = 'workflow_state';
    }
  }

  const timingStatus = isAppointment ? getAppointmentTimingStatus(item as Record<string, unknown>) : null;
  const isOperationallyComplete = isAppointment
    ? isAppointmentOperationallyComplete(item as Record<string, unknown>)
    : isTerminalWorkItemStatus(statusKey);

  if (
    isAppointment
    && includeTimingWarnings
    && source !== 'runtime_timer'
    && source !== 'workflow_state'
    && !isOperationallyComplete
    && !['ai_review', 'ai_on_hold'].includes(baseStatusKey)
  ) {
    if (timingStatus === 'missed') {
      statusKey = 'missed';
      source = 'timing_status';
    } else if (timingStatus === 'over_schedule') {
      statusKey = 'over_schedule';
      source = 'timing_status';
    } else if (timingStatus === 'onroute_late') {
      statusKey = 'running_late';
      source = 'timing_status';
    } else if (timingStatus === 'late') {
      statusKey = 'behind_schedule';
      source = 'timing_status';
    }
  }

  // For finance domain, use configLoader for terminal status check
  const isTerminal = isFinanceDomain
    ? registry.isTerminalStatus(itemType, statusKey)
    : isTerminalWorkItemStatus(statusKey);

  // For finance domain, try to get label from status definition
  let label: string;
  if (isFinanceDomain) {
    const statusDef = registry.getStatusDefinition(itemType, statusKey);
    label = statusDef?.label || formatStatusLabel(statusKey, item);
  } else {
    label = formatStatusLabel(statusKey, item);
  }

  return {
    rawStatusKey,
    baseStatusKey,
    statusKey,
    label,
    source,
    timingStatus,
    isTerminal,
    isOperationallyComplete,
    payment_status: resolvedPaymentStatus,
    is_system_managed: isSystemManaged,
    finance_domain: isFinanceDomain,
  };
}
